/********************************/
/*          About vuex          */
/********************************/
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from "vuex";
import { REQUEST_STATUS_COMPLETED, REQUEST_STATUS_FAILED } from "../../../../share/util/api/polling";
import individualDiagnose from '../../../../share/util/individualDiagnose';
import individualWorkSupportUtil from '../../../../share/util/individualWorkSupportUtil';
import session from '../../../../share/session';
import { apiErrorDefaultAction } from "../../../../share/util/api/errorAction";

/** 各フェーズ定義 */
const PHASE = {
  START: "start",
  EXECUTION: 'execution',
  COMPLETED: "completed",
  ERROR: "error",
};

/** API type定義 */
const API_TYPE = {
  STANDBY: "standby",
  WRITE: 'write',
  READ: "read",
  TERMINATE: "terminate",
  DISPOSE: "dispose"
};

/** 現在セッション */
const SESSION_PROPERTY = {
  DEFAULT: 0,
  NON_DEFAULT: 1
};

export default {
  name: "requirement153",
  data() {
    return {
      phase: PHASE.START,
      isLoading: false,
      systemId: "",
      modal_requirement_153: false,
      resolverAdjustment: "", // ユーザの画面入力
      validateResolverAdjustment: undefined, // ユーザ入力値のバリデーション結果
      sessionProperty: SESSION_PROPERTY.DEFAULT,
      adjustmentValue: '', //読み出した値
      adjustmentComplete: '', //変換後の値
    };
  },

  props: {
    i18nWsKey: { type: String, default: "", required: true },
    workSupportId: { type: Number, default: "", required: true }
  },

  computed: {
    ...mapState({
      netAppId: state => state.netAppConnector.pairingAppId
    }),
    /**
     * ダイアログのヘッダー色
     */
    headerColor() {
      switch (this.phase) {
        case PHASE.ERROR:
          return "danger";
        case PHASE.COMPLETED:
          return "success";
        case PHASE.START:
        case PHASE.EXECUTION:
        default:
          return "secondary";
      }
    },

    /**
     * 表示ヘッダー文字列判定
     * @returns {string} ヘッダー文字列
     */
    headerTitle() {
      const ConfirmTitle = 'message.common_title_confirmation';
      const ExecuteTitle = 'individual.common_title_execution';
      const SuccessTitle = 'message.common_title_success';
      const ErrorTitle = 'message.common_title_error';

      const titleMap = {
        [PHASE.START]: ConfirmTitle,
        [PHASE.EXECUTION]: ExecuteTitle,
        [PHASE.COMPLETED]: SuccessTitle,
        [PHASE.ERROR]: ErrorTitle,
      };

      return this.$t(titleMap[this.phase]);
    },

    /**
     * 作業サポート表示メッセージ判定
     * @returns {string} メッセージ
     */
    workSupportMessage() {
      const messageMap = {
        [PHASE.START]: `${this.i18nWsKey}.REQ_WS_153_TXT_1`,
        [PHASE.EXECUTION]: `${this.i18nWsKey}.REQ_WS_153_TXT_2`,
        [PHASE.COMPLETED]: `individual.label_resolver_adjustment_read_value`,
        [PHASE.ERROR]: `individual.error_communication_error`,
      };

      return this.$t(messageMap[this.phase]);
    },

    /**
     * [OK]ボタンの表示判定
     * @returns {boolean} trueなら表示
     */
    showButtonOk() {
      const showCancelButtonMap = {
        [PHASE.START]: true,
        [PHASE.EXECUTION]: false,
        [PHASE.COMPLETED]: true,
        [PHASE.ERROR]: true,
      };
      let result = showCancelButtonMap[this.phase];
      if (!result) {
        // 合致しない場合は false とする。
        result = false;
      }
      return result;
    },

    /**
     * [キャンセル]ボタンの表示判定
     * @returns {boolean} trueなら表示
     */
    showButtonCancel() {
      const showCancelButtonMap = {
        [PHASE.START]: true,
        [PHASE.EXECUTION]: false,
        [PHASE.COMPLETED]: false,
        [PHASE.ERROR]: false,
      };
      let result = showCancelButtonMap[this.phase];
      if (!result) {
        // 合致しない場合は false とする。
        result = false;
      }
      return result;
    },

    /**
     * [OK]ボタンの活性・非活性を判定
     */
    isDisableOk() {
      if (this.phase !== PHASE.START) {
        // 確認画面以外の時はローディング表示のみの判定
        return this.isLoading;
      }

      return this.isLoading || !(this.resolverAdjustment && this.resolverAdjustment.length === 8);
    }
  },

  methods: {
    ...mapActions("auth", ["logout", "resetSession"]),

    /** 初期化処理 */
    init() {
      this.systemId = individualDiagnose.getCurrentSystemId(this);
      this.sessionProperty = SESSION_PROPERTY.DEFAULT;
      this.modal_requirement_153 = true;
    },

    /** Wizardを閉じる */
    async closeWizard() {
      this.$emit("close-wizard");
      this.modal_requirement_153 = false;
    },

    /** その他エラー発生時の表示 */
    showUnexpectedError() {
      // 共通エラー表示
      this.phase = PHASE.ERROR;
    },

    /**
     * API 通信で共通バリデーションでエラーが発生した際のコールバック
     * @param {object} result バリデーション結果
     */
    validateErrorResult(result) {
      apiErrorDefaultAction(
        this,
        result,
        // ログアウト処理のコールバック
        (self) => { self; this.logout(this); },
        // セッションリセット処理のコールバック
        (self) => { self; this.resetSession(this); },
        // その他エラー発生のコールバック
        (result) => { result; this.showUnexpectedError(); },
      );
    },

    /**
     * 共通の[キャンセル]ボタンクリック処理
     */
    onCancelClicked() {
      // 待機画面のみにて表示のため
      this.closeWizard();
    },

    /**
     * 共通の[OK]ボタンクリック処理
     */
    onOkClicked() {
      switch (this.phase) {
        case PHASE.START:
          this.clickOkStart();
          break;
        case PHASE.COMPLETED:
          this.clickOkCompleted();
          break;
        case PHASE.ERROR:
          this.clickOkError();
          break;
        default:
          // noop
          break;
      }
    },

    /** 確認画面の「OK」ボタン押下 */
    async clickOkStart() {
      // 入力値をチェックする
      if (this.checkValidateResolverAdjustment()) {
        // standbyから処理を実行
        this.phase = PHASE.EXECUTION;
        await this.standby();
      }
    },

    /** 確認画面の「クリア」ボタン押下 */
    clearText() {
      this.resolverAdjustment = '';
      this.validateResolverAdjustment = undefined;
    },

    /** 完了画面の「OK」ボタン押下 */
    async clickOkCompleted() {
      try {
        this.isLoading = true;
        await this.terminate();
        // terminate APIの結果にかかわらず機能終了する
        await this.dispose();
        this.closeWizard();
      } finally {
        this.isLoading = false;
      }
    },

    /** 共通エラー画面の「OK」ボタン押下 */
    async clickOkError() {
      try {
        this.isLoading = true;
        if (this.sessionProperty === SESSION_PROPERTY.NON_DEFAULT) {
          await this.terminate();
        }
        // 結果にかかわらず終了する
        await this.dispose();
        this.closeWizard();
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * リゾルバ調整値のバリデーションチェック
     * @returns {boolean} 確認結果 (true: OK, false: NG)
     */
    checkValidateResolverAdjustment() {
      this.validateResolverAdjustment = undefined;

      // 入力値が8桁以外の場合エラー
      if (!this.resolverAdjustment || this.resolverAdjustment.length !== 8) {
        this.validateResolverAdjustment = { valid: true, msg: this.$t(`message.alert_validate_max_length`, ['8']) };
        return false;
      }

      // 不正文字列が含まれている場合はエラー
      if (!this.checkValidCharacter(this.resolverAdjustment)) {
        this.validateResolverAdjustment = { valid: true, msg: this.$t(`message.alert_validate_invalid_pattern`) };
        return false;
      }

      // OK判定
      return true;
    },

    /**
     * 使用可能な文字列のみかどうかの確認関数
     * @param code 対象code
     */
    checkValidCharacter(code) {
      // 0～9,A～Fのみが含まれているか確認する
      const halfReqExp = new RegExp(/^[0-9A-F]+$/);
      return halfReqExp.test(code);
    },

    /**
     * type: standby
     */
    async standby() {
      try {
        this.isLoading = true;
        // API実行パラメータ作成
        const token = session.getToken();
        const requestBody = {
          id_token: token.id_token,
          type: API_TYPE.STANDBY
        };

        // 初期処理（要求）
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          requestBody,
          // エラー発生のコールバックを実装する。
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        if (response) {
          switch (response.data.status) {
            case REQUEST_STATUS_COMPLETED:
              this.sessionProperty = SESSION_PROPERTY.NON_DEFAULT;
              // 書き込み処理を実施
              await this.write();
              break;
            case REQUEST_STATUS_FAILED:
            default:
              this.showUnexpectedError();
              break;
          }
        }
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * type: write
     */
    async write() {
      try {
        this.isLoading = true;
        // API実行パラメータ作成
        const token = session.getToken();
        const requestBody = {
          id_token: token.id_token,
          type: API_TYPE.WRITE,
          adjustment_value: this.resolverAdjustment // ユーザ入力値
        };

        // 初期処理（要求）
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          requestBody,
          // エラー発生のコールバックを実装する。
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        if (response) {
          switch (response.data.status) {
            case REQUEST_STATUS_COMPLETED:
              // 読み出し処理を実施
              await this.read();
              break;
            case REQUEST_STATUS_FAILED:
            default:
              this.showUnexpectedError();
              break;
          }
        }
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * type: read
     */
    async read() {
      try {
        this.isLoading = true;
        // API実行パラメータ作成
        const token = session.getToken();
        const requestBody = {
          id_token: token.id_token,
          type: API_TYPE.READ,
        };

        // 初期処理（要求）
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          requestBody,
          // エラー発生のコールバックを実装する。
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        if (response) {
          switch (response.data.status) {
            case REQUEST_STATUS_COMPLETED: {
              this.adjustmentValue = response.data.user_data.adjustment_value;
              const readOutData = await individualWorkSupportUtil.getReadOutData(this, this.systemId, response.data.user_data);
              this.adjustmentComplete = readOutData.split(":")[1];
              // 完了画面へ遷移
              this.phase = PHASE.COMPLETED;
              break;
            }
            case REQUEST_STATUS_FAILED:
            default:
              this.showUnexpectedError();
              break;
          }
        }
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * type: terminate
     */
    async terminate() {
      // API実行パラメータ作成
      const token = session.getToken();
      const requestBody = {
        id_token: token.id_token,
        type: API_TYPE.TERMINATE,
      };

      // 初期処理（要求）
      const response = await individualWorkSupportUtil.pollingWspApi(
        this,
        this.netAppId,
        this.systemId,
        this.workSupportId,
        requestBody,
        // エラー発生のコールバックを実装する。
        // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
        (errorType) => { errorType; this.showUnexpectedError(); },
        // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
        (result) => { this.validateErrorResult(result); },
        // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
        (result) => { this.validateErrorResult(result); },
      );

      if (response) {
        switch (response.data.status) {
          case REQUEST_STATUS_COMPLETED:
            this.sessionProperty = SESSION_PROPERTY.DEFAULT;
            break;
          case REQUEST_STATUS_FAILED:
          default:
            if (!this.phase === PHASE.COMPLETED) {
              // 成功画面から実施した場合は通信エラー画面に遷移しない
              this.showUnexpectedError();
            }
            break;
        }
      }
    },

    /** 
     * API_TYPE: disposeの実行 
     * エラー処理が異なるため独自実装
     */
    async dispose() {
      const token = session.getToken();
      const requestBody = {
        id_token: token.id_token,
        type: API_TYPE.DISPOSE
      };

      await individualWorkSupportUtil.pollingWspApi(
        this,
        this.netAppId,
        this.systemId,
        this.workSupportId,
        requestBody,
        // エラー発生のコールバックを実装する。
        // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
        (errorType) => { errorType; this.showUnexpectedError(); },
        // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
        (result) => { this.validateErrorResult(result); },
        // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
        (result) => { this.validateErrorResult(result); },
      );
    },
  },

  async mounted() {
    this.init();
  }
};
